import BaseService from './BaseService';
import {
  Campaign,
  DecisionPayload,
  DecisionRecord,
  PushResponse,
  ReviewResult,
  ReviewRow,
  ReviewScope,
  RetryJudgment,
  RunListItem,
} from 'pages/SearchTermReview/types';

class SearchTermReviewService extends BaseService {
  // Unlike the shared handleHttpRequest (which swallows errors and returns null),
  // this surfaces the backend error message so the page can show WHY a run failed
  // instead of silently doing nothing.
  async runReview(scope: ReviewScope): Promise<{ data: ReviewResult | null; error: string | null }> {
    await this._getAccessToken();

    try {
      const resp = await this.http.get('search-term-review', { params: scope });
      return { data: resp.data as ReviewResult, error: null };
    } catch (err: any) {
      const body = err?.response?.data;
      const message =
        (body && (body.message || body.error || (typeof body === 'string' ? body : null))) ||
        err?.message ||
        'Request failed';
      const status = err?.response?.status;
      return { data: null, error: status ? `${message} (HTTP ${status})` : String(message) };
    }
  }

  // runId (when present) attaches the judgments to that run's history, so reopening
  // the audit later restores the AI results without paying for them again.
  async retryAi(terms: ReviewRow[], runId?: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<{ judgments: RetryJudgment[]; decisions: Record<string, DecisionRecord | null> }>(
      () => this.http.post('search-term-review/retry', { terms, runId }),
      'Failed to retry AI judgment',
    );
  }

  // Push APPROVED rows to Google Ads (admin token required). The server re-verifies
  // every approval against the decisions log; outside production it runs Google Ads
  // validateOnly dry-runs. Surfaces the backend error message like runReview does.
  async pushToGoogleAds(rows: ReviewRow[], reviewer: string): Promise<{ data: PushResponse | null; error: string | null }> {
    await this._getAccessToken();

    try {
      const resp = await this.http.post('search-term-review/push', { rows, reviewer });
      return { data: resp.data as PushResponse, error: null };
    } catch (err: any) {
      const body = err?.response?.data;
      const message =
        (body && (body.message || body.error || (typeof body === 'string' ? body : null))) ||
        err?.message ||
        'Push failed';
      const status = err?.response?.status;
      return { data: null, error: status ? `${message} (HTTP ${status})` : String(message) };
    }
  }

  // The selectable campaigns for a market — populates the audit-scope pick-list.
  async listCampaigns(store: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<{ campaigns: Campaign[] }>(
      () => this.http.get('search-term-review/campaigns', { params: { store } }),
      'Failed to load campaigns',
    );
  }

  // The persisted audit history log (most recent first).
  async listRuns() {
    await this._getAccessToken();

    return this.handleHttpRequest<{ runs: RunListItem[] }>(
      () => this.http.get('search-term-review/runs'),
      'Failed to load audit history',
    );
  }

  // Reopen a persisted run — stored AI judgments and CURRENT decisions come merged.
  async getRun(runId: string) {
    await this._getAccessToken();

    return this.handleHttpRequest<ReviewResult>(
      () => this.http.get(`search-term-review/runs/${encodeURIComponent(runId)}`),
      'Failed to load the saved audit',
    );
  }

  // Lazy-load a page of the low-value "monitor" tail (not returned in the initial run).
  async loadMonitor(scope: ReviewScope, monitorPage: number) {
    await this._getAccessToken();

    return this.handleHttpRequest<ReviewResult>(
      () => this.http.get('search-term-review', { params: { ...scope, monitorPage } }),
      'Failed to load monitor terms',
    );
  }

  async saveDecision(payload: DecisionPayload) {
    await this._getAccessToken();

    return this.handleHttpRequest<{ termKey: string } & DecisionRecord>(
      () => this.http.post('search-term-review/decisions', payload),
      'Failed to save decision',
    );
  }

  // Clear a decision (toggle the same choice off). This is itself a logged decision, so
  // it carries the reviewer and is recorded with source 'dashboard' server-side.
  async clearDecision(payload: {
    store: string;
    campaign_id: string;
    ad_group_id: string;
    search_term: string;
    triggering_keyword?: string | null;
    match_type?: string | null;
    reviewer: string;
    recommendation_type?: string | null;
  }) {
    await this._getAccessToken();

    return this.handleHttpRequest<{ termKey: string }>(
      () => this.http.post('search-term-review/decisions', { ...payload, decision: 'clear' }),
      'Failed to clear decision',
    );
  }
}

export default SearchTermReviewService;
